import type { AdSenseConfigOptions, RuntimeConfig } from "./types";
import { normalizeApiBaseUrl } from "./url";

declare const __ADSENSE_BASE_URL__: string | undefined;
declare const __ADSENSE_ENV__: string | undefined;

const DEV_BASE_URL = "https://api.adsense.raha.af/api";
const DEFAULT_TIMEOUT_MS = 9000;

function readBuildBaseUrl(): string | undefined {
  return typeof __ADSENSE_BASE_URL__ === "string"
    ? __ADSENSE_BASE_URL__.trim()
    : undefined;
}

function readBuildEnv(): string {
  return typeof __ADSENSE_ENV__ === "string" ? __ADSENSE_ENV__ : "development";
}

function isLocalhostUrl(value: string): boolean {
  try {
    const url = new URL(value);

    return url.hostname === "localhost" || url.hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

function resolveDefaultBaseUrl(): string {
  const buildBaseUrl = readBuildBaseUrl();

  if (buildBaseUrl) {
    return buildBaseUrl;
  }

  return readBuildEnv() === "production" ? "" : DEV_BASE_URL;
}

export function createRuntimeConfig(
  options: AdSenseConfigOptions = {},
  previous?: RuntimeConfig,
): RuntimeConfig {
  const baseUrl =
    options.baseUrl?.trim() || previous?.baseUrl || resolveDefaultBaseUrl();
  const debug = options.debug ?? previous?.debug ?? false;
  const timeoutMs =
    options.timeoutMs ?? previous?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const lazyLoad = options.lazyLoad ?? previous?.lazyLoad ?? false;
  const privacy = options.privacy ?? previous?.privacy;
  const video = options.video ?? previous?.video;

  return {
    baseUrl: normalizeApiBaseUrl(baseUrl),
    debug,
    timeoutMs,
    lazyLoad,
    privacy,
    video,
  };
}

export function getConfigError(config: RuntimeConfig): string | undefined {
  if (!config.baseUrl) {
    return "Missing AdSense baseUrl. The publisher-tag.js build must include a default API base URL.";
  }

  try {
    const url = new URL(config.baseUrl);

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return "Invalid AdSense baseUrl. Expected an absolute HTTP or HTTPS URL.";
    }
  } catch {
    return "Invalid AdSense baseUrl. Expected an absolute HTTP or HTTPS URL.";
  }

  if (readBuildEnv() === "production" && isLocalhostUrl(config.baseUrl)) {
    return "Invalid AdSense baseUrl. Production publisher-tag.js builds must not use localhost.";
  }

  if (!Number.isFinite(config.timeoutMs) || config.timeoutMs <= 0) {
    return "Invalid AdSense timeoutMs. Expected a positive number.";
  }

  return undefined;
}

export function debugWarn(config: RuntimeConfig, message: string): void {
  if (config.debug) {
    console.warn(`[AdSense] ${message}`);
  }
}
